import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { FaPrint, FaSearch, FaFilter } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const API_BASE = "https://nissi-india.com/api";

const PurchaseReqReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [reqNo, setReqNo] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [projectName, setProjectName] = useState("");
  const [store, setStore] = useState("");
  const [status, setStatus] = useState("Waiting");
  const [location, setLocation] = useState("");

  const [storeOptions, setStoreOptions] = useState([]);
  const [projectNameOptions, setProjectNameOptions] = useState([]);
  const [projectCodeOptions, setProjectCodeOptions] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // =========================================================
  // Session values
  // =========================================================

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";

  const branch = (
    localStorage.getItem("branchType") || ""
  ).toUpperCase();

  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    ]
  );

  // =========================================================
  // Branch type
  // =========================================================

  const initialBranchType =
    branch === "HO" ? "HO" : "Branch";

  const [branchType, setBranchType] = useState(
    initialBranchType
  );

  useEffect(() => {
    setBranchType(initialBranchType);

    setLocation(
      initialBranchType === "HO"
        ? "HO"
        : "BRANCH"
    );
  }, [initialBranchType]);

  // =========================================================
  // Helpers
  // =========================================================

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const [y, m, d] = dateStr.split("-");

    return `${d}/${m}/${y}`;
  };

  const getRowId = (item) =>
    item.fld_PurchaseRequestid ??
    item.fld_purchaserequestid ??
    item.fld_PurchaseRequestID ??
    null;

  const getReqNo = (item) =>
    item.fld_purchaseRequestNo ??
    item.fld_PurchaseRequestNo ??
    item.fld_purchaserequestno ??
    "";

  const handleBranchTypeChange = (value) => {
    setBranchType(value);

    setLocation(
      value === "HO"
        ? "HO"
        : "BRANCH"
    );
  };

  const clearFilters = () => {
    setFromDate("");

    setToDate(
      new Date()
        .toISOString()
        .split("T")[0]
    );

    setReqNo("");
    setProjectCode("");
    setProjectName("");
    setStore("");
    setStatus("Waiting");
    setResults([]);
  };

  // =========================================================
  // Print
  // =========================================================

  const handlePrint = (item) => {
    const id = getRowId(item);

    if (!id) {
      toast.error(
        "Purchase Request ID not found"
      );
      return;
    }

    const printUrl =
      `/inventory/Purchase_Print?id=${id}`;

    window.open(
      printUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleEnquiryPrint = (item) => {
    const id = getRowId(item);

    if (!id) {
      toast.error(
        "Purchase Request ID not found"
      );
      return;
    }

    const printUrl =
      `/inventory/Purchase_EnquiryPrint?id=${id}`;

    window.open(
      printUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // =========================================================
  // Load Store
  // =========================================================

  useEffect(() => {
    const fetchStoreOptions = async () => {
      try {
        const res = await axios.post(
          `${API_BASE}/getFromStoreOptions`,
          {
            branchtype: branch || "HO",
            branchid: branchId,
          },
          {
            headers: commonHeaders,
          }
        );

        setStoreOptions(
          Array.isArray(res.data?.storeOptions)
            ? res.data.storeOptions
            : []
        );
      } catch (error) {
        console.error(
          "Store load error:",
          error
        );

        toast.error(
          "Failed to load store options"
        );
      }
    };

    if (branchId) {
      fetchStoreOptions();
    }
  }, [
    branchId,
    branch,
    commonHeaders,
  ]);

  // =========================================================
  // Load Project Names
  // =========================================================

  useEffect(() => {
    const fetchProjectNames = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/prReport_getProjectName`,
          {
            params: {
              projectId: "Select",
            },
            headers: commonHeaders,
          }
        );

        setProjectNameOptions(
          Array.isArray(res.data?.data)
            ? res.data.data
            : []
        );
      } catch (error) {
        console.error(
          "Project name load error:",
          error
        );

        toast.error(
          "Failed to load project names"
        );
      }
    };

    fetchProjectNames();
  }, [commonHeaders]);

  // =========================================================
  // Load Project Codes
  // =========================================================

  useEffect(() => {
    const fetchProjectCodes = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/prReport_getProjectCode`,
          {
            params: {
              projectId: "Select",
            },
            headers: commonHeaders,
          }
        );

        setProjectCodeOptions(
          Array.isArray(res.data?.data)
            ? res.data.data
            : []
        );
      } catch (error) {
        console.error(
          "Project code load error:",
          error
        );

        toast.error(
          "Failed to load project codes"
        );
      }
    };

    fetchProjectCodes();
  }, [commonHeaders]);

  // =========================================================
  // Search
  // =========================================================

  const handleSearch = async () => {
    if (!store) {
      toast.warn(
        "Please select a Store"
      );
      return;
    }

    const params = {
      branchType: location,
      branchId,

      userBranchType:
        location || "HO",

      brType:
        branchType === "HO"
          ? "H"
          : "B",

      reqNo:
        reqNo.trim(),

      fromDate:
        fromDate
          ? formatDate(fromDate)
          : "",

      toDate:
        toDate
          ? formatDate(toDate)
          : "",

      fromStoreId:
        store,

      projectId:
        projectCode ||
        projectName ||
        0,

      status,
    };

    try {
      setLoading(true);

      const res = await axios.get(
        `${API_BASE}/purchaseStoreMaterialList`,
        {
          params,
          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        throw new Error(
          res.data?.message ||
          "Failed to load report"
        );
      }

      const data =
        Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setResults(data);

      toast.success(
        `${
          res.data?.total ??
          data.length
        } requisition(s) loaded`
      );
    } catch (err) {
      console.error(
        "purchaseStoreMaterialList error:",
        err
      );

      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch listing"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // View All
  // =========================================================

  const handleViewAll = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${API_BASE}/purchaseStoreMaterialList`,
        {
          params: {
            branchId,

            branchType:
              location || "HO",

            userBranchType:
              location || "HO",

            brType:
              branchType === "HO"
                ? "H"
                : "B",

            reqNo: "",
            fromDate: "",
            toDate: "",
            fromStoreId: "Select",
            projectId: 0,
            status: "Select",
          },

          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        throw new Error(
          res.data?.message ||
          "Failed to load report"
        );
      }

      const data =
        Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setResults(data);

      toast.success(
        `${
          res.data?.total ??
          data.length
        } requisition(s) loaded`
      );
    } catch (err) {
      console.error(
        "View all error:",
        err
      );

      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch all records"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <LayoutWrapper>
      <HeaderBar
        title="PURCHASE / STORE / MATERIAL REQUISITION REPORT"
      />

      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* ===================================================
            PAGE HEADER
        =================================================== */}

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1
              className={`text-2xl font-bold ${
                isDark
                  ? "text-white"
                  : "text-gray-900"
              }`}
            >
              Purchase Requisition Report
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {results.length} Records
            </span>
          </div>

          <button
            onClick={() =>
              setShowFilters(
                (prev) => !prev
              )
            }
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? `border-gray-600 text-white hover:bg-gray-700 ${
                    showFilters
                      ? "bg-gray-700"
                      : ""
                  }`
                : `border-gray-300 text-gray-700 hover:bg-gray-100 ${
                    showFilters
                      ? "bg-gray-100"
                      : ""
                  }`
            }`}
          >
            <FaFilter className="w-4 h-4" />

            {showFilters
              ? "Hide Filters"
              : "Show Filters"}
          </button>
        </div>

        {/* ===================================================
            BRANCH TYPE
        =================================================== */}

        <div className="mb-6">
          <label
            className={`block text-sm font-medium mb-3 ${
              isDark
                ? "text-gray-300"
                : "text-gray-700"
            }`}
          >
            Branch Type
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() =>
                handleBranchTypeChange(
                  "HO"
                )
              }
              disabled={branch !== "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                branchType === "HO"
                  ? `${buttonClass} ${hoverClass} text-white`
                  : isDark
                  ? "bg-gray-700 text-gray-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              } ${
                branch !== "HO"
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              Head Office
            </button>

            <button
              type="button"
              onClick={() =>
                handleBranchTypeChange(
                  "Branch"
                )
              }
              disabled={branch === "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                branchType === "Branch"
                  ? `${buttonClass} ${hoverClass} text-white`
                  : isDark
                  ? "bg-gray-700 text-gray-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              } ${
                branch === "HO"
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              Branch
            </button>
          </div>
        </div>

        {/* ===================================================
            FILTERS
        =================================================== */}

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDark
                  ? "text-white"
                  : "text-gray-900"
              }`}
            >
              Search Filters
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* From Date */}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
                >
                  From Date
                </label>

                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) =>
                    setFromDate(
                      e.target.value
                    )
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              {/* To Date */}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
                >
                  To Date
                </label>

                <input
                  type="date"
                  value={toDate}
                  onChange={(e) =>
                    setToDate(
                      e.target.value
                    )
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              {/* Req No */}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
                >
                  Req No
                </label>

                <input
                  type="text"
                  value={reqNo}
                  onChange={(e) =>
                    setReqNo(
                      e.target.value
                    )
                  }
                  placeholder="Enter request number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              {/* Project Code */}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
                >
                  Project Code
                </label>

                <select
                  value={projectCode}
                  onChange={(e) => {
                    setProjectCode(
                      e.target.value
                    );

                    setProjectName("");
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">
                    Select Project Code
                  </option>

                  {projectCodeOptions.map(
                    (option) => (
                      <option
                        key={
                          option.id
                        }
                        value={
                          option.id
                        }
                      >
                        {option.code}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Project Name */}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
                >
                  Project Name
                </label>

                <select
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(
                      e.target.value
                    );

                    setProjectCode("");
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">
                    Select Project Name
                  </option>

                  {projectNameOptions.map(
                    (option) => (
                      <option
                        key={
                          option.id
                        }
                        value={
                          option.id
                        }
                      >
                        {option.name}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Store */}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
                >
                  Store{" "}
                  <span className="text-red-500">
                    *
                  </span>
                </label>

                <select
                  value={store}
                  onChange={(e) =>
                    setStore(
                      e.target.value
                    )
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">
                    Select Store
                  </option>

                  {storeOptions.map(
                    (s) => (
                      <option
                        key={
                          s.fld_storeid
                        }
                        value={
                          s.fld_storeid
                        }
                      >
                        {
                          s.fld_storename
                        }
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Status */}

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
                >
                  Status
                </label>

                <select
                  value={status}
                  onChange={(e) =>
                    setStatus(
                      e.target.value
                    )
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Waiting">
                    Waiting
                  </option>

                  <option value="Cancel">
                    Cancel
                  </option>

                  <option value="Close">
                    Close
                  </option>

                  <option value="Approved">
                    Approved
                  </option>

                  <option value="DisApproved">
                    DisApproved
                  </option>
                </select>
              </div>
            </div>

            {/* Buttons */}

            <div
              className={`flex justify-between items-center pt-4 border-t ${
                isDark
                  ? "border-gray-600"
                  : "border-gray-200"
              }`}
            >
              <button
                type="button"
                onClick={clearFilters}
                className={`px-4 py-2 font-medium ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Clear Filters
              </button>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors disabled:opacity-50`}
                >
                  <FaSearch className="w-4 h-4" />

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>

                <button
                  type="button"
                  onClick={handleViewAll}
                  disabled={loading}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors disabled:opacity-50"
                >
                  <FaSearch className="w-4 h-4" />

                  View All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================
            RESULTS
        =================================================== */}

        {loading ? (
          <div className="text-center py-12 text-gray-500">
            Loading requisitions...
          </div>
        ) : results.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`p-4 border-b ${
                isDark
                  ? "border-gray-700"
                  : "border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold ${
                  isDark
                    ? "text-white"
                    : "text-gray-900"
                }`}
              >
                Requisition Results (
                {results.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead
                  className={`bg-${baseColor}-${baseShade}`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Sl No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Req No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Type
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Indent Ref
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Request Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Quotation Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Account Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      MD Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      PO Status
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Print
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Enquiry
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-700 bg-gray-800"
                      : "divide-gray-200 bg-white"
                  }`}
                >
                  {results.map(
                    (item, idx) => {
                      const requestStatus =
                        item.requeststatus ??
                        item.purchaserequeststatus ??
                        item.reqstatus ??
                        item.purchaseorderstatus ??
                        "Waiting";

                      const quotationStatus =
                        item.quotationstatus ??
                        "Waiting";

                      const accountStatus =
                        item.accountsstatus ??
                        "Waiting";

                      const mdStatus =
                        item.mdstatus ??
                        "Waiting";

                      const poStatus =
                        item.purchaseorderstatus ??
                        "Waiting";

                      const statusClass = (
                        value
                      ) => {
                        if (
                          value ===
                          "Approved"
                        ) {
                          return "text-green-600";
                        }

                        if (
                          value ===
                            "DisApproved" ||
                          value ===
                            "Cancelled" ||
                          value ===
                            "Cancel"
                        ) {
                          return "text-red-600";
                        }

                        return isDark
                          ? "text-yellow-400"
                          : "text-yellow-600";
                      };

                      return (
                        <tr
                          key={
                            getRowId(
                              item
                            ) ||
                            getReqNo(
                              item
                            ) ||
                            idx
                          }
                          className={`transition-colors ${
                            isDark
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-900"
                            }`}
                          >
                            {idx + 1}
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-900"
                            }`}
                          >
                            {getReqNo(
                              item
                            )}
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-900"
                            }`}
                          >
                            {
                              item.fld_date
                            }
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-900"
                            }`}
                          >
                            {
                              item.fld_type
                            }
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-900"
                            }`}
                          >
                            {
                              item.fld_indentref
                            }
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${statusClass(
                              requestStatus
                            )}`}
                          >
                            {
                              requestStatus
                            }
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${statusClass(
                              quotationStatus
                            )}`}
                          >
                            {
                              quotationStatus
                            }
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${statusClass(
                              accountStatus
                            )}`}
                          >
                            {
                              accountStatus
                            }
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${statusClass(
                              mdStatus
                            )}`}
                          >
                            {mdStatus}
                          </td>

                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${statusClass(
                              poStatus
                            )}`}
                          >
                            {poStatus}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() =>
                                handlePrint(
                                  item
                                )
                              }
                              className={`p-2 rounded transition-colors ${
                                isDark
                                  ? "text-blue-400 hover:text-blue-300 hover:bg-gray-700"
                                  : "text-blue-600 hover:text-blue-800 hover:bg-gray-100"
                              }`}
                              title="Print Requisition"
                            >
                              <FaPrint className="w-4 h-4" />
                            </button>
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() =>
                                handleEnquiryPrint(
                                  item
                                )
                              }
                              className={`p-2 rounded transition-colors ${
                                isDark
                                  ? "text-green-400 hover:text-green-300 hover:bg-gray-700"
                                  : "text-green-600 hover:text-green-800 hover:bg-gray-100"
                              }`}
                              title="Print Enquiry"
                            >
                              <FaPrint className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          showFilters && (
            <div
              className={`text-center py-12 ${
                isDark
                  ? "text-gray-400"
                  : "text-gray-500"
              }`}
            >
              <p>
                No records found. Please
                use the filters to search
                for purchase requisitions.
              </p>
            </div>
          )
        )}
      </div>
    </LayoutWrapper>
  );
};

export default PurchaseReqReport;